"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import Header from "../components/Header"
import "./Profile.css"
import { Dumbbell, Clock, ImageIcon, MapPin, CreditCard, CheckCircle, ArrowRight, Target, TrendingUp, Award, Users } from 'lucide-react'

const GymProfile = () => {
  const navigate = useNavigate()
  const [gymProgress, setGymProgress] = useState(0)
  const [gymData, setGymData] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchGymData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fetch`, {
        headers: {
          auth: document.cookie.replace(/(?:(?:^|.*;\s*)auth\s*=\s*([^;]*).*$)|^.*$/, "$1"),
        },
      })
      setGymProgress(response.data.gym.complete)
      setGymData(response.data.gym)
    } catch (error) {
      console.error("Error fetching gym data:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchGymData()
  }, [])

  const handleNavigate = (path) => {
    navigate(path)
  }

  const steps = [
    {
      icon: Dumbbell,
      title: "Add Equipment",
      description: "List all the equipment available in your gym to attract customers.",
      path: "/equipment",
      progress: 20,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      icon: Clock,
      title: "Add Time Slots",
      description: "Define time slots for gym sessions, classes, or personal training.",
      path: "/slots",
      progress: 20,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
    },
    {
      icon: CreditCard,
      title: "Bank Details",
      description: "Provide bank details for secure payment processing.",
      path: "/banking",
      progress: 20,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      icon: ImageIcon,
      title: "Upload Gallery",
      description: "Upload high-quality images of your gym to attract more clients.",
      path: "/gallery",
      progress: 20,
      color: "from-orange-500 to-orange-600",
      bgColor: "bg-orange-50",
      iconColor: "text-orange-600",
    },
    {
      icon: MapPin,
      title: "Update Location",
      description: "Ensure your gym is easy to find by adding your exact location.",
      path: "/gallery",
      progress: 20,
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-50",
      iconColor: "text-red-600",
    },
  ]

  const completedSteps = steps.filter((step) => gymProgress >= step.progress).length
  const progressPercentage = (completedSteps / steps.length) * 100

  if (loading) {
    return (
      <div className="gym-profile-page">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your gym profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="gym-profile-page">
      <Header />

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1>Welcome to Your Gym Dashboard</h1>
            <p>Complete your profile setup to start attracting customers and growing your business</p>
          </div>
          <div className="hero-stats">
            <div className="stat-card">
              <Target className="stat-icon" />
              <div className="stat-info">
                <span className="stat-number">{gymProgress}%</span>
                <span className="stat-label">Complete</span>
              </div>
            </div>
            <div className="stat-card">
              <TrendingUp className="stat-icon" />
              <div className="stat-info">
                <span className="stat-number">{completedSteps}/5</span>
                <span className="stat-label">Steps Done</span>
              </div>
            </div>
            <div className="stat-card">
              <Users className="stat-icon" />
              <div className="stat-info">
                <span className="stat-number">0</span>
                <span className="stat-label">Members</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content">
        {/* Progress Section */}
        <div className="progress-section">
          <div className="progress-header">
            <div className="progress-title">
              <h2>Profile Completion</h2>
              <span className="progress-percentage">{gymProgress}%</span>
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${gymProgress}%` }}
                  data-progress={gymProgress}
                ></div>
              </div>
            </div>
          </div>

          <div className="progress-steps">
            {steps.map((step, index) => {
              const StepIcon = step.icon
              const isCompleted = gymProgress >= (index + 1) * 20
              return (
                <div
                  key={index}
                  className={`progress-step ${isCompleted ? "completed" : ""}`}
                  onClick={() => handleNavigate(step.path)}
                >
                  <div className={`step-icon-wrapper ${step.bgColor}`}>
                    <StepIcon className={`step-icon ${step.iconColor}`} />
                  </div>
                  <div className="step-content">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                  </div>
                  <div className="step-status">
                    {isCompleted ? (
                      <CheckCircle className="status-icon completed" />
                    ) : (
                      <ArrowRight className="status-icon pending" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div className={`completion-message ${gymProgress === 100 ? "success" : "warning"}`}>
            {gymProgress === 100 ? (
              <>
                <Award className="message-icon" />
                <div className="message-content">
                  <h3>Congratulations! 🎉</h3>
                  <p>Your gym profile is complete and ready for business. Start attracting customers now!</p>
                </div>
              </>
            ) : (
              <>
                <Target className="message-icon" />
                <div className="message-content">
                  <h3>Almost There!</h3>
                  <p>Complete {5 - completedSteps} more steps to make your gym profile ready for customers.</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action Cards Grid */}
        <div className="action-cards-section">
          <h2 className="section-title">Quick Actions</h2>
          <div className="action-cards-grid">
            {steps.map((step, index) => {
              const StepIcon = step.icon
              const isCompleted = gymProgress >= (index + 1) * 20
              return (
                <div
                  key={index}
                  className={`action-card ${isCompleted ? "completed" : ""}`}
                  onClick={() => handleNavigate(step.path)}
                >
                  <div className="card-header">
                    <div className={`card-icon-wrapper bg-gradient-to-br ${step.color}`}>
                      <StepIcon className="card-icon" />
                    </div>
                    {isCompleted && <CheckCircle className="completion-badge" />}
                  </div>
                  <div className="card-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                  <div className="card-footer">
                    <button className="action-button">
                      {isCompleted ? "Edit" : "Setup"}
                      <ArrowRight className="button-icon" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GymProfile
